import { ReturnAllIcon } from '@/assets/icons/return-all-icon'
import { TextInput } from '../inputs/text-input'
import { Button } from '../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { AdvanceAllIcon } from '@/assets/icons/advance-all-icon'
import { useCallback, type ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { mergeObjectToSearchParams } from '@/utils/merge-object-to-search-params'

export interface PaginationTableProps {
  setPage: (page: string) => void
  setLimit: (limit: string) => void
  page: string
  limit: string
  totalPages: string
  isLoading?: boolean
}

export const PaginationTable = ({
  setPage,
  page,
  limit,
  totalPages,
  isLoading,
}: PaginationTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const handleLimit = (limit: string) => {
    mergeObjectToSearchParams(searchParams, setSearchParams, [
      ['page', '1'],
      ['limit', limit],
    ])
  }

  const handlePage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const pageNumber = parseInt(value)
      if (pageNumber > parseInt(totalPages)) {
        setPage(totalPages)
      } else {
        return pageNumber === 0 ? setPage('1') : setPage(value)
      }
    },
    [setPage, totalPages],
  )

  const goToLastPage = useCallback(() => {
    setPage(totalPages)
  }, [setPage])

  const goToFirstPage = useCallback(() => {
    setPage('1')
  }, [setPage])

  const nextPage = useCallback(() => {
    if (Number(page) < Number(totalPages)) {
      setPage(String(Number(page) + 1))
    }
  }, [setPage, page, totalPages])

  const handlePageOnBlur = () => {
    if (page === '' || page === '0') {
      setPage('1')
    }
  }

  const backPage = useCallback(() => {
    if (Number(page) > 1) {
      setPage(String(Number(page) - 1))
    }
  }, [setPage, page])

  return (
    <div className="flex justify-between">
      <div className="flex gap-2 items-center text-base">
        Itens por Página
        <Select defaultValue={limit} onValueChange={handleLimit}>
          <SelectTrigger className="min-w-20">
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5" defaultChecked>
              5
            </SelectItem>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="15">15</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-10">
        <div className="flex items-center gap-3 text-base">
          Página:
          <div className="flex items-center gap-1">
            <div className="w-15">
              <TextInput
                onBlur={handlePageOnBlur}
                onChange={handlePage}
                value={page}
                className="text-base text-center h-10"
              ></TextInput>
            </div>
            -<div>{isLoading ? '...' : totalPages}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            onClick={goToFirstPage}
            variant={'ghost'}
            className="h-10 w-10"
          >
            <ReturnAllIcon />
          </Button>
          <div className="flex gap-3">
            <Button
              onClick={backPage}
              variant={'outline'}
              className="h-10 w-10 shadow-md"
            >
              {'<'}
            </Button>
            <Button
              onClick={nextPage}
              variant={'outline'}
              className="h-10 w-10 shadow-md"
            >
              {'>'}
            </Button>
          </div>
          <Button
            onClick={goToLastPage}
            variant={'ghost'}
            className="w-10 h-10"
          >
            <AdvanceAllIcon className="text-4xl" />
          </Button>
        </div>
      </div>
    </div>
  )
}
